import React, { forwardRef } from 'react';
import { Typography } from '../Typography/Typography';
import styles from './Button.module.css';

/* 
 * Button Component - Figma Design System Implementation
 * Supports all button variants from Figma: primary, outline, link, secondary-link, CTA
 * Uses exact design tokens and accessibility features
 */

export interface ButtonProps {
  children: React.ReactNode;
  
  // Button variants from Figma
  variant: 'primary' | 'outline' | 'link' | 'secondary-link' | 'cta' | 'filter' | 'nav' | 'tertiary';
  
  // Property-specific theming from Figma
  property?: 'atacama' | 'patagonia' | 'iguazu' | 'mendoza' | 'santa-catarina';
  
  // Sizes
  size?: 'small' | 'medium' | 'large';
  
  // States
  disabled?: boolean;
  loading?: boolean;
  active?: boolean;
  
  // Behavior
  type?: 'button' | 'submit' | 'reset';
  href?: string;
  target?: '_blank' | '_self' | '_parent' | '_top';
  download?: string;
  
  // Styling
  className?: string;
  style?: React.CSSProperties;
  fullWidth?: boolean;
  
  // Icons
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  
  // Events
  onClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  
  // Accessibility
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-expanded'?: boolean;
  'aria-pressed'?: boolean;
  id?: string;
}

// Helper function to combine class names
const cn = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(' ');
};

/**
 * Universal Button Component
 * Implements all Figma button patterns with exact design system specifications
 */
export const Button = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(({
  children,
  variant,
  property,
  size = 'medium',
  disabled = false,
  loading = false,
  active = false,
  type = 'button',
  href,
  target,
  download,
  className,
  style,
  fullWidth = false,
  leftIcon,
  rightIcon,
  onClick,
  onFocus,
  onBlur,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-expanded': ariaExpanded,
  'aria-pressed': ariaPressed,
  id,
  ...props
}, ref) => {
  
  // Determine if this should be a link or button
  const isLink = Boolean(href && !disabled);
  const Component = isLink ? 'a' : 'button';
  
  // Handle click events
  const handleClick = (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (disabled || loading) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };
  
  // Handle keyboard events for accessibility
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled || loading) return;
    
    // For links acting as buttons, handle Enter and Space
    if (isLink && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick?.(event as any);
    }
  };

  // Common props for both button and anchor
  const commonProps = {
    id,
    className: cn(
      styles.button,
      styles[`button--${variant}`],
      styles[`button--${size}`],
      property && styles[`button--${property}`],
      disabled && styles['button--disabled'],
      loading && styles['button--loading'],
      active && styles['button--active'],
      fullWidth && styles['button--full-width'],
      className
    ),
    style,
    onClick: handleClick,
    onFocus,
    onBlur,
    onKeyDown: handleKeyDown,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    'aria-expanded': ariaExpanded,
    'aria-pressed': ariaPressed,
    'aria-disabled': disabled,
    ...props
  };

  // Button-specific props
  const buttonProps = {
    ...commonProps,
    type: type as 'button' | 'submit' | 'reset',
    disabled: disabled || loading,
    ref: ref as React.Ref<HTMLButtonElement>
  };

  // Link-specific props
  const linkProps = {
    ...commonProps,
    href,
    target,
    download,
    role: 'button',
    tabIndex: disabled ? -1 : 0,
    ref: ref as React.Ref<HTMLAnchorElement>
  };

  // Render loading spinner
  const LoadingSpinner = () => (
    <span className={styles.button__spinner} aria-hidden="true">
      <span className={styles.spinner}></span>
    </span>
  );

  // Render button content
  const ButtonContent = () => (
    <>
      {leftIcon && (
        <span className={styles.button__icon_left} aria-hidden="true">
          {leftIcon}
        </span>
      )}
      
      {loading && <LoadingSpinner />}
      
      <Typography
        variant="cta-label"
        as="span"
        className={styles.button__text}
      >
        {children}
      </Typography>
      
      {rightIcon && (
        <span className={styles.button__icon_right} aria-hidden="true">
          {rightIcon}
        </span>
      )}
    </>
  );

  // Render as link or button
  if (isLink) {
    return (
      <a {...linkProps}>
        <ButtonContent />
      </a>
    );
  }

  return (
    <button {...buttonProps}>
      <ButtonContent />
    </button>
  );
});

Button.displayName = 'Button';

// Specialized button components for common use cases
export const PrimaryButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
  <Button variant="primary" {...props} />
);

export const OutlineButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
  <Button variant="outline" {...props} />
);

export const LinkButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
  <Button variant="link" {...props} />
);

export const CTAButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
  <Button variant="cta" {...props} />
);

export const FilterButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
  <Button variant="filter" {...props} />
);

export const NavButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
  <Button variant="nav" {...props} />
);

export const TertiaryButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
  <Button variant="tertiary" {...props} />
);

// Property-specific button components
export const AtacamaButton: React.FC<Omit<ButtonProps, 'property'>> = (props) => (
  <Button property="atacama" {...props} />
);

export const PatagoniaButton: React.FC<Omit<ButtonProps, 'property'>> = (props) => (
  <Button property="patagonia" {...props} />
);

export const IguazuButton: React.FC<Omit<ButtonProps, 'property'>> = (props) => (
  <Button property="iguazu" {...props} />
);

export const MendozaButton: React.FC<Omit<ButtonProps, 'property'>> = (props) => (
  <Button property="mendoza" {...props} />
);

export const SantaCatarinaButton: React.FC<Omit<ButtonProps, 'property'>> = (props) => (
  <Button property="santa-catarina" {...props} />
);

export default Button;